import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Send, Loader2 } from "lucide-react";
import { DIVISIONI } from "@/data/content";
import { Reveal, GoldLine, SectionHeading } from "@/components/Reveal";
import { useLang } from "@/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full bg-[#1D1D1D] border border-[#A5A5A5]/25 text-[#F5F2EB] placeholder-[#A5A5A5]/50 px-5 py-4 text-sm font-light focus:outline-none focus:border-[#E5C776] focus:ring-1 focus:ring-[#E5C776] transition-colors duration-300";

const Field = ({ label, children, htmlFor }) => (
  <div>
    <label htmlFor={htmlFor} className="block eyebrow mb-3 !text-[10px]">
      {label}
    </label>
    {children}
  </div>
);

export default function Contatti() {
  const { t } = useLang();
  const [form, setForm] = useState({
    nome: "",
    email: "",
    azienda: "",
    tipo: "partnership",
    divisione: "",
    messaggio: "",
  });
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/contatti`, {
        ...form,
        azienda: form.azienda || null,
        divisione: form.divisione || null,
      });
      toast.success(t("form.successTitle"), {
        description: t("form.successDesc"),
      });
      setForm({ nome: "", email: "", azienda: "", tipo: "partnership", divisione: "", messaggio: "" });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(t("form.errorTitle"), {
        description: typeof detail === "string" ? detail : t("form.errorDesc"),
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <main data-testid="contatti-page" className="bg-[#080808] min-h-screen">
      <section className="pt-40 pb-24 lg:pb-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-16">
          <div className="lg:col-span-5">
            <SectionHeading eyebrow={t("contatti.eyebrow")} title={t("contatti.title")} goldWord={t("contatti.gold")} />
            <Reveal delay={0.2}>
              <p className="mt-10 text-lg font-light text-[#A5A5A5] leading-relaxed">
                {t("contatti.intro")}
              </p>
            </Reveal>
            <Reveal delay={0.3}>
              <div className="mt-12 space-y-8">
                <div>
                  <p className="eyebrow mb-2 !text-[10px]">Presenza</p>
                  <p className="text-sm text-[#F5F2EB] font-light">Più sedi in Italia</p>
                </div>
                <div className="gold-line w-16 opacity-60" />
                <div>
                  <p className="eyebrow mb-2 !text-[10px]">Business Unit</p>
                  <p className="text-sm text-[#F5F2EB] font-light">
                    Food · Luxury · Hospitality · Real Estate · Beauty · New Ventures
                  </p>
                </div>
                <div className="gold-line w-16 opacity-60" />
                <div>
                  <p className="eyebrow mb-2 !text-[10px]">Area Riservata</p>
                  <p className="text-sm text-[#A5A5A5] font-light">
                    Partner e aziende del gruppo accedono dalla sezione dedicata.
                  </p>
                </div>
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-7">
            <Reveal delay={0.15}>
              <form
                onSubmit={submit}
                data-testid="contact-form"
                className="bg-[#141414] border border-[#C6A45D]/20 p-8 sm:p-12 space-y-8"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
                  <Field label={`${t("form.nome")} *`} htmlFor="nome">
                    <input
                      id="nome"
                      data-testid="contact-input-nome"
                      required
                      minLength={2}
                      value={form.nome}
                      onChange={set("nome")}
                      placeholder="Il vostro nome"
                      className={inputCls}
                    />
                  </Field>
                  <Field label={`${t("form.email")} *`} htmlFor="email">
                    <input
                      id="email"
                      data-testid="contact-input-email"
                      type="email"
                      required
                      value={form.email}
                      onChange={set("email")}
                      placeholder="nome@azienda.it"
                      className={inputCls}
                    />
                  </Field>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
                  <Field label={t("form.azienda")} htmlFor="azienda">
                    <input
                      id="azienda"
                      data-testid="contact-input-azienda"
                      value={form.azienda}
                      onChange={set("azienda")}
                      placeholder="Nome dell'organizzazione"
                      className={inputCls}
                    />
                  </Field>
                  <Field label={`${t("form.tipo")} *`} htmlFor="tipo">
                    <select
                      id="tipo"
                      data-testid="contact-select-tipo"
                      value={form.tipo}
                      onChange={set("tipo")}
                      className={`${inputCls} appearance-none cursor-pointer`}
                    >
                      <option value="partnership">{t("form.tipoPartnership")}</option>
                      <option value="investimento">{t("form.tipoInvestimento")}</option>
                      <option value="stampa">{t("form.tipoStampa")}</option>
                      <option value="generale">{t("form.tipoGenerale")}</option>
                    </select>
                  </Field>
                </div>
                <Field label={t("form.unit")} htmlFor="divisione">
                  <select
                    id="divisione"
                    data-testid="contact-select-divisione"
                    value={form.divisione}
                    onChange={set("divisione")}
                    className={`${inputCls} appearance-none cursor-pointer`}
                  >
                    <option value="">{t("form.noPref")}</option>
                    {DIVISIONI.map((d) => (
                      <option key={d.id} value={d.nome}>
                        Billions {d.nome}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field label={`${t("form.messaggio")} *`} htmlFor="messaggio">
                  <textarea
                    id="messaggio"
                    data-testid="contact-input-messaggio"
                    required
                    minLength={10}
                    rows={6}
                    value={form.messaggio}
                    onChange={set("messaggio")}
                    placeholder="Raccontateci il vostro progetto o la vostra richiesta..."
                    className={`${inputCls} resize-none`}
                  />
                </Field>
                <button
                  type="submit"
                  data-testid="contact-submit-button"
                  disabled={sending}
                  className="group w-full sm:w-auto inline-flex items-center justify-center gap-3 px-10 py-4 bg-[#C6A45D] text-[#080808] text-xs tracking-[0.25em] uppercase font-semibold hover:bg-[#E5C776] disabled:opacity-60 disabled:cursor-not-allowed transition-colors duration-300"
                >
                  {sending ? (
                    <>
                      <Loader2 size={15} className="animate-spin" />
                      {t("form.invio")}
                    </>
                  ) : (
                    <>
                      {t("form.invia")}
                      <Send size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
                    </>
                  )}
                </button>
              </form>
            </Reveal>
          </div>
        </div>
      </section>
    </main>
  );
}
